/* eslint-disable react/jsx-pascal-case */
import { actions } from 'react-redux-form';
import React, {
  useEffect,
  Fragment,
  useContext,
  useCallback,
  useRef
} from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  Card,
  CardBody,
  Button,
  Label,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Input,
  UncontrolledTooltip
} from 'reactstrap';
import { ListGroup, ListGroupItem } from 'reactstrap';
import { Loading } from './LoadingComponent';
import { CSVLink, CSVDownload } from 'react-csv';
import Pagination from './Pagination';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import DOMPurify from '../utils/domPurifyConfig';
import { handleHtmlTagAutoComplete, checkHtmlTagAutoComplete } from '../utils/htmlTagAutoComplete';
import { handleArithmeticInput } from '../utils/arithmeticEval';
import { orderWithPinnedFirst } from '../utils/pinnedSort';
import { usePinEnabled } from '../utils/usePinEnabled';
import { useColumnWidths } from '../utils/useColumnWidths';
import { useManualResizeEnabled } from '../utils/useManualResizeEnabled';
import { handleTextareaImagePaste, selectImageTagOnDoubleClick } from '../utils/imagePasteResize';
import { renderMarkdown } from '../utils/renderMarkdown';
import ImageLightbox from './ImageLightbox';
import ColumnResizeHandle from './ColumnResizeHandle';

// Helper function to extract search keywords (excluding exclusion keywords with !)
function extractSearchKeywords(keywordString) {
  if (!keywordString) return [];

  const keywordAndArray = keywordString.split('|');
  const allKeywords = [];

  for (let k = 0; k < keywordAndArray.length; k++) {
    const keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
    for (let i = 0; i < keywordArray.length; i++) {
      const kw = keywordArray[i];
      if (kw && kw !== ' ' && kw !== '' && kw.split('')[0] !== '!') {
        // Add keyword (lowercased for multi-char, as-is for single char)
        if (kw.length === 1) {
          allKeywords.push(kw);
        } else {
          allKeywords.push(kw.toLowerCase());
        }
      }
    }
  }

  // Remove duplicates
  return [...new Set(allKeywords)];
}

// Helper function to highlight keywords in plain text
function highlightText(text, keywords) {
  if (!text || !keywords || keywords.length === 0) return text;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  // Create regex pattern - case insensitive for multi-char keywords
  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split by matches and wrap matches with <mark>
  const parts = text.split(regex);

  return parts.map((part, index) => {
    const isMatch = keywords.some(kw =>
      kw.length === 1 ? part === kw : part.toLowerCase() === kw.toLowerCase()
    );
    if (isMatch) {
      return <mark key={index} style={{ backgroundColor: '#ffff00', padding: 0 }}>{part}</mark>;
    }
    return part;
  });
}

// Helper function to highlight keywords in HTML string (for dangerouslySetInnerHTML)
function highlightHTML(htmlString, keywords) {
  if (!htmlString || !keywords || keywords.length === 0) return htmlString;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split HTML into tags and text portions
  // This regex matches HTML tags
  const tagRegex = /(<[^>]*>)/g;
  const parts = htmlString.split(tagRegex);

  // Process each part - only highlight in text portions (not tags)
  const highlighted = parts.map(part => {
    if (part.startsWith('<') && part.endsWith('>')) {
      // This is an HTML tag - don't modify
      return part;
    }
    // This is text content - highlight keywords
    return part.replace(regex, '<mark style="background-color:#ffff00;padding:0">$1</mark>');
  });

  return highlighted.join('');
}

function RenderInputTable ({
  input,
  deleteInput,
  updateInput,
  resetInputForm,
  auth,
  updatingId,
  isFullContentShown,
  toggleIsFullContentShown,
  saveScrollPosition,
  restoreScrollPosition,
  searchKeywords,
  keyword,
  view,
  getColumnWidth,
  startResize,
  htmlResumeRevision
}) {
  // Check if THIS specific row is being updated
  const isThisRowUpdating = updatingId === input._id;
  const syncFormElementsToHTML = containerId => {
    const container = document.querySelector(
      `tr[data-row-id="${containerId}"]`
    );
    if (!container) return null;

    const reasonCell = container.querySelector('td[data-cellid$="|reason"]');
    const conditionCell = container.querySelector(
      'td[data-cellid$="|condition"]'
    );

    const syncCell = (cell, field) => {
      if (!cell) return null;

      // [name] targets the actual rendered content div, not the width
      // wrapper div the resize feature adds around reason/condition.
      const div = cell.querySelector('div[name]');
      const targetElement = div || cell;

      // Sync all form elements to their attributes
      targetElement.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        if (cb.checked) {
          cb.setAttribute('checked', 'checked');
        } else {
          cb.removeAttribute('checked');
        }
      });

      targetElement.querySelectorAll('input[type="radio"]').forEach(radio => {
        if (radio.checked) {
          radio.setAttribute('checked', 'checked');
        } else {
          radio.removeAttribute('checked');
        }
      });

      targetElement.querySelectorAll('select').forEach(sel => {
        sel.querySelectorAll('option').forEach(opt => {
          if (opt.value === sel.value) {
            opt.setAttribute('selected', 'selected');
          } else {
            opt.removeAttribute('selected');
          }
        });
      });

      targetElement
        .querySelectorAll(
          'input[type="text"], input[type="number"], input[type="email"], input[type="url"], input[type="tel"], input[type="date"], input[type="time"], input[type="datetime-local"]'
        )
        .forEach(input => {
          input.setAttribute('value', input.value);
        });

      // DON'T sync the main editing textarea - only embedded textareas
      targetElement.querySelectorAll('textarea').forEach(textarea => {
        const isMainEditor = textarea.name === field &&
          ((field === 'reason' && isReasonEditing) ||
            (field === 'condition' && isConditionEditing));
        if (!isMainEditor) {
          textarea.textContent = textarea.value;
        }
      });

      return div ? div.innerHTML : cell.innerHTML;
    };

    return {
      reason: syncCell(reasonCell, 'reason'),
      condition: syncCell(conditionCell, 'condition')
    };
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const itemColors = useSelector(state => state.fieldButtons.itemColors);
  const dispatch = useDispatch();

  // State for context menu (button click)
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0, openUpward: true });
  const menuButtonRef = useRef(null);

  // Click outside to close menu
  useEffect(() => {
    if (!showContextMenu) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.context-menu')) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showContextMenu]);

  // Version counter to force re-render of dangerouslySetInnerHTML content
  const [contentVersion, setContentVersion] = useState(0);

  // Optimistic UI: store pending HTML while waiting for server response
  const [pendingReason, setPendingReason] = useState(null);
  const [pendingCondition, setPendingCondition] = useState(null);

  // Update version when reason or condition changes from props
  // Also clear pending state when server confirms the update
  useEffect(() => {
    setContentVersion(v => v + 1);
    setPendingReason(null); // Clear pending - server has confirmed
  }, [input.reason]);

  useEffect(() => {
    setContentVersion(v => v + 1);
    setPendingCondition(null); // Clear pending - server has confirmed
  }, [input.condition]);

  // フォームデータをローカルstateで管理
  const [formData, setFormData] = useState({
    date: input.date,
    place: input.place,
    person: input.person,
    subject: input.subject,
    reason: input.reason,
    condition: input.condition,
    data: input.data,
    unit: input.unit
  });

  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        const ismobile = window.innerWidth < 768;
        if (ismobile !== isMobile) setIsMobile(ismobile);
      },
      false
    );
  }, [isMobile]);

  // モーダルを開く時にフォームデータをリセット
  const toggleModal = () => {
    if (!isOpen) {
      setFormData({
        date: input.date,
        place: input.place,
        person: input.person,
        subject: input.subject,
        reason: input.reason,
        condition: input.condition,
        data: input.data,
        unit: input.unit
      });
    }
    setIsOpen(!isOpen);
  };

  // フォーム入力の変更を処理
  const handleInputChange = field => e => {
    const newValue = e.target.value;
    const prevValue = formData[field] || ''; // Capture previous value

    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));

    // textareaの高さを自動調整 & HTML tag auto-completion
    if (field === 'reason' || field === 'condition') {
      e.target.style.height = 'auto';
      e.target.style.height = e.target.scrollHeight + 'px';

      // HTML tag auto-completion
      const textarea = e.target;
      const cursorPos = textarea.selectionStart;
      const result = checkHtmlTagAutoComplete(newValue, cursorPos, prevValue);
      if (result) {
        setFormData(prev => ({
          ...prev,
          [field]: result.newValue
        }));
        setTimeout(() => {
          textarea.selectionStart = result.cursorPos;
          textarea.selectionEnd = result.cursorPos;
          textarea.focus();
        }, 0);
      }

      // Arithmetic evaluation (e.g. "1+1=" -> "1+1=2")
      handleArithmeticInput(textarea, newValue, newVal => {
        setFormData(prev => ({
          ...prev,
          [field]: newVal
        }));
      }, prevValue);
    }
  };

  // フォーム送信を処理
  // フォーム送信を処理
  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    toggleModal();

    if (window.confirm('OK to change data?')) {
      updateInput(
        input._id,
        formData.date,
        formData.place,
        formData.person,
        formData.subject,
        formData.reason,
        formData.condition,
        formData.data,
        formData.unit
      );
    }
  };

  // Handle menu button click to show context menu
  const handleMenuButtonClick = (event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    // The menu's content only ever takes one of two shapes (owner-only
    // actions are shown or not), so its height is known up front — no need
    // to measure it after the fact, which would reposition it visibly.
    // It normally opens upward (anchored above the button); if there isn't
    // enough room above the header, open it downward instead.
    const isOwner = auth.id === input.user;
    const menuHeight = isOwner ? 220 : 100;
    const headerEl = document.querySelector('.header');
    const safeTop = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
    const openUpward = rect.top - safeTop >= menuHeight;
    // Anchor with `bottom` (not a translateY transform) when opening upward,
    // so the entrance animation's `transform: scale(...)` doesn't clobber a
    // separate positioning transform and cause a visible jump once it ends.
    setMenuPosition({
      x: rect.left,
      top: openUpward ? undefined : rect.bottom,
      bottom: openUpward ? window.innerHeight - rect.top : undefined,
      openUpward
    });
    setShowContextMenu(true);
  };

  const handleClick = (event, id) => {
    try {
      // Let interactive descendants (details, buttons, inputs …) behave normally
      if (event.target.closest('details,button,input,select,textarea,a,.context-menu')) return;

      // Toggle background color (allows multiple selections)
      const newColor = itemColors[id] === '#d6eaf8' ? 'white' : '#d6eaf8';

      dispatch({
        type: 'SET_ITEM_COLOR',
        payload: { id, color: newColor }
      });

      if (newColor === '#d6eaf8') {
        // Selecting: Update browser URL
        const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
        const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
        const linkPath = `/inputs?id=${id}${keywordParam}${viewParam}`;
        window.history.pushState({}, '', linkPath);

        // Copy row data to input form using react-redux-form actions.
        // Date is deliberately left blank: duplicating a row almost always
        // means entering a new date, so leaving the old one in just adds an
        // extra "clear the date" step every time.
        dispatch(actions.change('input.date', ''));
        dispatch(actions.change('input.place', input.place));
        dispatch(actions.change('input.person', input.person));
        dispatch(actions.change('input.subject', input.subject));
        dispatch(actions.change('input.reason', input.reason));
        dispatch(actions.change('input.condition', input.condition));
        dispatch(actions.change('input.data', input.data));
        dispatch(actions.change('input.unit', input.unit));

        // Save to localStorage (date left blank - see comment above)
        const formData = {
          date: '',
          place: input.place || '',
          person: input.person || '',
          subject: input.subject || '',
          reason: input.reason || '',
          condition: input.condition || '',
          data: input.data || '',
          unit: input.unit || ''
        };
        localStorage.setItem('dataForm', JSON.stringify(formData));

        // Dispatch custom event to update X button visibility in DataComponent
        try {
          window.dispatchEvent(new CustomEvent('dataFormRowSelected', { detail: { formData } }));
        } catch (eventErr) {
          console.error('Failed to dispatch dataFormRowSelected event:', eventErr);
        }
      } else {
        // Deselecting: Clear the input form
        dispatch(actions.change('input.date', ''));
        dispatch(actions.change('input.place', ''));
        dispatch(actions.change('input.person', ''));
        dispatch(actions.change('input.subject', ''));
        dispatch(actions.change('input.reason', ''));
        dispatch(actions.change('input.condition', ''));
        dispatch(actions.change('input.data', ''));
        dispatch(actions.change('input.unit', ''));

        // Clear localStorage
        localStorage.removeItem('dataForm');

        // Dispatch custom event to clear X button visibility in DataComponent
        const emptyFormData = {
          date: '', place: '', person: '', subject: '',
          reason: '', condition: '', data: '', unit: ''
        };
        try {
          window.dispatchEvent(new CustomEvent('dataFormRowSelected', { detail: { formData: emptyFormData } }));
        } catch (eventErr) {
          console.error('Failed to dispatch dataFormRowSelected event:', eventErr);
        }
      }
    } catch (err) {
      console.error('Error in handleClick:', err);
    }
  };

  // Context menu actions
  const handleMenuEdit = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    toggleModal();
  };

  const handleMenuDelete = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    if (window.confirm('Are you sure?')) {
      deleteInput(input._id);
    }
  };

  const handleMenuCopyLink = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
    const linkPath = `/inputs?id=${input._id}${keywordParam}${viewParam}`;
    const fullUrl = window.location.origin + linkPath;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleMenuTogglePin = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    updateInput(
      input._id,
      input.date,
      input.place,
      input.person,
      input.subject,
      input.reason,
      input.condition,
      input.data,
      input.unit,
      !input.pinned
    );
  };

  const handleLinkClick = event => {
    event.stopPropagation();
  };

  const [isDateEditing, setIsDateEditing] = useState(false);
  const [isPlaceEditing, setIsPlaceEditing] = useState(false);
  const [isPersonEditing, setIsPersonEditing] = useState(false);
  const [isSubjectEditing, setIsSubjectEditing] = useState(false);
  const [isReasonEditing, setIsReasonEditing] = useState(false);
  const [isConditionEditing, setIsConditionEditing] = useState(false);
  const [isDataEditing, setIsDataEditing] = useState(false);
  const [isUnitEditing, setIsUnitEditing] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const handleImageClick = e => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      e.stopPropagation();
      setLightboxImage({
        src: e.target.src,
        width: e.target.offsetWidth,
        height: e.target.offsetHeight
      });
    }
  };

  const [tempValue, setTempValue] = useState('');

  const handleDoubleClick = e => {
    const fieldName = e.target.getAttribute('name');
    const fieldValue = input[fieldName];
    setTempValue(fieldValue);
    if (fieldName === 'date') setIsDateEditing(true);
    if (fieldName === 'place') setIsPlaceEditing(true);
    if (fieldName === 'person') setIsPersonEditing(true);
    if (fieldName === 'subject') setIsSubjectEditing(true);
    if (fieldName === 'reason') setIsReasonEditing(true);
    if (fieldName === 'condition') setIsConditionEditing(true);
    if (fieldName === 'data') setIsDataEditing(true);
    if (fieldName === 'unit') setIsUnitEditing(true);
  };

  const handleChange = e => {
    const newValue = e.target.value;
    const prevValue = tempValue; // Capture previous value before update
    setTempValue(newValue);

    // textareaの高さを自動調整（reason/conditionフィールド用）
    if (e.target.tagName === 'TEXTAREA') {
      e.target.style.height = 'auto';
      e.target.style.height = e.target.scrollHeight + 'px';
    }

    // HTML tag auto-completion for reason/condition fields (works for both textarea and input)
    const fieldName = e.target.name;
    if (fieldName === 'reason' || fieldName === 'condition') {
      handleHtmlTagAutoComplete(e.target, newValue, setTempValue, prevValue);
      handleArithmeticInput(e.target, newValue, setTempValue, prevValue);
    }
  };

  const handleBlur = e => {
    if (!document.hasFocus()) {
      return;
    }
    if (
      !(
        isDateEditing ||
        isPlaceEditing ||
        isPersonEditing ||
        isSubjectEditing ||
        isReasonEditing ||
        isConditionEditing ||
        isDataEditing ||
        isUnitEditing
      )
    ) {
      return;
    }
    const fieldName = e.target.name;

    if (tempValue !== input[fieldName]) {
      // For reason and condition fields, use the textarea value directly
      // DON'T sync embedded form elements when editing the main field
      const newInput = {
        ...input,
        [fieldName]: tempValue
      };

      if (window.confirm('OK to change data?')) {
        if (saveScrollPosition) {
          saveScrollPosition();
        }

        updateInput(
          input._id,
          newInput.date,
          newInput.place,
          newInput.person,
          newInput.subject,
          newInput.reason,
          newInput.condition,
          newInput.data,
          newInput.unit
        ).then(() => {
          if (restoreScrollPosition) {
            restoreScrollPosition();
          }
        });
      }
    }

    if (fieldName === 'date') setIsDateEditing(false);
    if (fieldName === 'place') setIsPlaceEditing(false);
    if (fieldName === 'person') setIsPersonEditing(false);
    if (fieldName === 'subject') setIsSubjectEditing(false);
    if (fieldName === 'reason') setIsReasonEditing(false);
    if (fieldName === 'condition') setIsConditionEditing(false);
    if (fieldName === 'data') setIsDataEditing(false);
    if (fieldName === 'unit') setIsUnitEditing(false);
  };

  const handleKeyDown = event => {
    // Only single-line fields (date, place, ...) need Enter blocked; reason/
    // condition edit as a <textarea>, where Enter is supposed to insert a
    // newline. This listener lives on the <td> and catches Enter bubbling up
    // from whichever field is focused inside it, so without this check it
    // silently ate every newline typed into reason/condition, collapsing
    // multi-line content (e.g. a LaTeX $$...$$ block, which needs real line
    // breaks around the delimiters) onto a single line.
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  const isNaNStyle = isNaN(input.data.replace(/,/g, ''))
    ? {}
    : { textAlign: 'right' };
  const backgroundStyle = { backgroundColor: itemColors[input._id] || 'white' };
  const dataStyle = { ...isNaNStyle, ...backgroundStyle };

  const containerRef = useRef(null);
  const pendingCellRef = useRef(null); // Track the cell being edited
  const originalHTMLRef = useRef(null); // Store original HTML for cancel
  const isConfirmingRef = useRef(false);

  // Handler for "click outside cell" to trigger confirmation
  useEffect(() => {
    // Helper to start tracking a cell for editing
    const startTrackingCell = (td, field, inputId) => {
      if (!pendingCellRef.current || pendingCellRef.current.td !== td) {
        // [name] targets the actual rendered content div, not the width
        // wrapper div the resize feature adds around reason/condition.
        const div = td.querySelector('div[name]');
        originalHTMLRef.current = div ? div.innerHTML : td.innerHTML;
        pendingCellRef.current = { td, field, inputId };
      }
    };

    // Double-click on time/date inputs to clear their value
    // (iOS Safari's "reset" only resets to the HTML attribute value, not to empty)
    const handleDoubleClickInput = e => {
      const target = e.target;
      if (target.tagName !== 'INPUT') return;

      const inputType = target.type;
      if (inputType !== 'time' && inputType !== 'date' && inputType !== 'datetime-local') return;

      // Only offer to clear if the input has a value
      if (!target.value) return;

      const td = target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [inputId, field] = cellId.split('|');

      if (field !== 'reason' && field !== 'condition') return;
      if (inputId !== input._id) return;

      e.preventDefault();
      e.stopPropagation();

      if (window.confirm('Clear this value?')) {
        target.value = '';
        target.setAttribute('value', '');
        // Start tracking to capture the change
        startTrackingCell(td, field, inputId);
      }
    };

    // Track when user starts editing a cell (focus on any form element)
    const handleFocusIn = e => {
      if (
        !e.target ||
        !(
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'SELECT' ||
          e.target.tagName === 'TEXTAREA'
        )
      ) {
        return;
      }

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [inputId, field] = cellId.split('|');

      // Only handle reason and condition fields
      if (field !== 'reason' && field !== 'condition') return;
      if (inputId !== input._id) return;

      // Skip if we're editing the main textarea itself
      if (
        (field === 'reason' && isReasonEditing && e.target.name === 'reason') ||
        (field === 'condition' && isConditionEditing && e.target.name === 'condition')
      ) {
        return;
      }

      startTrackingCell(td, field, inputId);
    };

    // Also track changes on checkboxes/radios/selects (they may not trigger focusin on iOS)
    const handleChange = e => {
      if (
        !e.target ||
        !(
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'SELECT'
        )
      ) {
        return;
      }

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [inputId, field] = cellId.split('|');

      // Only handle reason and condition fields
      if (field !== 'reason' && field !== 'condition') return;
      if (inputId !== input._id) return;

      startTrackingCell(td, field, inputId);
    };

    // Track input events (fires more reliably than change, especially for time/date inputs on iOS)
    const handleInput = e => {
      if (!e.target || !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [inputId, field] = cellId.split('|');

      if (field !== 'reason' && field !== 'condition') return;
      if (inputId !== input._id) return;
      if (
        (field === 'reason' && isReasonEditing && e.target.name === 'reason') ||
        (field === 'condition' && isConditionEditing && e.target.name === 'condition')
      ) return;

      startTrackingCell(td, field, inputId);

      // Keep embedded textareas' live text reflected in their serialized HTML.
      // Do not mirror an input's live value into its `value` attribute here:
      // iOS uses that attribute as a date/time picker's Reset baseline, so
      // changing it while the picker is open can make Reset restore the current
      // selection instead of clearing it.
      // Inputs are synced to their attributes when the edit is committed.
      if (e.target.tagName === 'TEXTAREA' && (
        e.target.name !== field ||
        !(field === 'reason' ? isReasonEditing : isConditionEditing)
      )) {
        e.target.textContent = e.target.value;
      }

    };

    // If the app is backgrounded or closed while an embedded control is still
    // focused, save its current HTML without showing a confirmation dialog.
    const flushPendingEdit = event => {
      if (event && event.type === 'visibilitychange' && !document.hidden) return;
      const pending = pendingCellRef.current;
      if (!pending || !pending.td.isConnected) return;

      const { field, inputId } = pending;
      const values = syncFormElementsToHTML(inputId);
      const newValue = values && values[field];
      if (newValue == null || newValue === originalHTMLRef.current) return;

      const updated = { ...input, [field]: newValue };
      pendingCellRef.current = null;
      originalHTMLRef.current = null;
      updateInput(
        inputId,
        updated.date,
        updated.place,
        updated.person,
        updated.subject,
        updated.reason,
        updated.condition,
        updated.data,
        updated.unit,
        updated.pinned
      );
    };

    // Handle click outside to confirm changes
    const handleClickOutside = e => {
      if (isConfirmingRef.current) return;
      if (!pendingCellRef.current) return;

      // A column-resize drag isn't "clicking outside" the cell being edited -
      // it's a capture-phase listener so it'd otherwise fire (and pop the
      // confirm() dialog below) before the resize handle's own handler runs.
      if (e.target.closest && e.target.closest('.column-resize-handle')) return;

      // Skip if we're in direct edit mode (double-click editing)
      // The handleBlur will handle saving in that case
      if (isReasonEditing || isConditionEditing) {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        return;
      }

      const pending = pendingCellRef.current;
      const { td, field, inputId } = pending;

      // The cell's DOM node can be gone (re-render, pagination, search) while
      // this ref is still holding on to it - without this, every future
      // click anywhere on the page (e.g. the hamburger menu button) would
      // keep misfiring into the "confirm changes" flow below for a cell that
      // no longer exists, since a detached node can never "contain" the new
      // click target.
      if (!td.isConnected) {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        return;
      }

      // Check if click is inside the same cell
      if (td.contains(e.target)) {
        return; // Still inside the cell, don't confirm yet
      }

      // Everything below reads the embedded field's current value, so it has
      // to wait until the browser's native blur for this tap has actually
      // happened - this mousedown/touchstart capture-phase listener runs
      // BEFORE that (confirmed: activeElement is still the old field here,
      // and its blur only fires once this event finishes dispatching), so
      // reading .value synchronously here can catch an in-progress IME
      // composition (e.g. Japanese kana-to-kanji conversion, but this can
      // also just be a plain <input type="text">) before the browser commits
      // its last, uncommitted characters into the value on blur - silently
      // dropping exactly the most recently typed content. Deferring with
      // setTimeout(0) runs after that commit instead.
      setTimeout(() => {
        // Something else may have already claimed or cleared this exact
        // pending cell while we were waiting (e.g. focus moved straight to
        // another tracked field) - if so, leave it alone.
        if (pendingCellRef.current !== pending) return;

        // Sync and confirm.
        // [name] targets the actual rendered content div, not the width
        // wrapper div the resize feature adds around reason/condition.
        const div = td.querySelector('div[name]');
        const container = div || td;

        // Sync all form elements to their HTML attributes
        container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          if (cb.checked) {
            cb.setAttribute('checked', 'checked');
          } else {
            cb.removeAttribute('checked');
          }
        });

        container.querySelectorAll('input[type="radio"]').forEach(radio => {
          if (radio.checked) {
            radio.setAttribute('checked', 'checked');
          } else {
            radio.removeAttribute('checked');
          }
        });

        container.querySelectorAll('select').forEach(sel => {
          sel.querySelectorAll('option').forEach(opt => {
            if (opt.value === sel.value) {
              opt.setAttribute('selected', 'selected');
            } else {
              opt.removeAttribute('selected');
            }
          });
        });

        container
          .querySelectorAll(
            'input[type="text"], input[type="number"], input[type="email"], input[type="url"], input[type="tel"], input[type="date"], input[type="time"], input[type="datetime-local"]'
          )
          .forEach(inp => {
            inp.setAttribute('value', inp.value);
          });

        container.querySelectorAll('textarea').forEach(textarea => {
          if (!textarea.hasAttribute('name')) {
            textarea.textContent = textarea.value;
          }
        });

        // Get the updated HTML
        const newValue = div ? div.innerHTML : td.innerHTML;

        // Check if anything actually changed
        if (newValue === originalHTMLRef.current) {
          // No changes, just clear the pending state
          pendingCellRef.current = null;
          originalHTMLRef.current = null;
          return;
        }

        const updated = { ...input, [field]: newValue };

        isConfirmingRef.current = true;

        if (window.confirm('OK to change data?')) {
          // Optimistic UI: immediately show the new value while waiting for server
          if (field === 'reason') {
            setPendingReason(newValue);
          } else if (field === 'condition') {
            setPendingCondition(newValue);
          }

          updateInput(
            inputId,
            updated.date,
            updated.place,
            updated.person,
            updated.subject,
            updated.reason,
            updated.condition,
            updated.data,
            updated.unit
          );
        } else {
          // User canceled - restore original HTML
          if (div) {
            div.innerHTML = originalHTMLRef.current;
          } else {
            td.innerHTML = originalHTMLRef.current;
          }
        }

        isConfirmingRef.current = false;
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
      }, 0);
    };

    const rowContainer = containerRef.current;
    if (rowContainer) {
      rowContainer.addEventListener('focusin', handleFocusIn, true);
      // Also listen for change events to track checkbox/radio/select changes on iOS
      rowContainer.addEventListener('change', handleChange, true);
      // Listen for input events (fires more reliably for time/date inputs, especially on iOS reset)
      rowContainer.addEventListener('input', handleInput, true);
      // Double-click on time/date inputs to clear them
      rowContainer.addEventListener('dblclick', handleDoubleClickInput, true);
    }

    // Listen for clicks/touches on the entire document to detect "click outside"
    // Use both mousedown and touchstart for cross-platform support
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    window.addEventListener('pagehide', flushPendingEdit);
    document.addEventListener('visibilitychange', flushPendingEdit);

    return () => {
      if (rowContainer) {
        rowContainer.removeEventListener('focusin', handleFocusIn, true);
        rowContainer.removeEventListener('change', handleChange, true);
        rowContainer.removeEventListener('input', handleInput, true);
        rowContainer.removeEventListener('dblclick', handleDoubleClickInput, true);
      }
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
      window.removeEventListener('pagehide', flushPendingEdit);
      document.removeEventListener('visibilitychange', flushPendingEdit);
    };
  }, [input, updateInput, isReasonEditing, isConditionEditing]);

  return (
    <tr
      ref={containerRef}
      data-row-id={input._id}
      style={{ backgroundColor: itemColors[input._id] || 'white' }}
    >
      <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      {/* Context Menu - rendered via portal to avoid table layout issues */}
      {showContextMenu && createPortal(
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: menuPosition.x,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1000,
            fontSize: '13px',
            userSelect: 'none',
            minWidth: '180px',
            overflow: 'hidden',
            animation: 'menuScaleIn 150ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            transformOrigin: menuPosition.openUpward ? 'bottom left' : 'top left'
          }}
        >
          <style>{`
            @keyframes menuScaleIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
            .context-menu-item:hover {
              background-color: #f0f0f0;
            }
            .context-menu-item-delete:hover {
              background-color: rgba(163, 45, 45, 0.08);
            }
          `}</style>
          {/* Header */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: '#f8f9fa',
            fontSize: '11px',
            color: '#666',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {input.subject ? (input.subject.length > 25 ? input.subject.substring(0, 25) + '...' : input.subject) : 'Record'}
          </div>

          {/* Safe Actions */}
          {auth.id === input.user && (
            <div
              className="context-menu-item"
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onClick={() => { handleMenuEdit(); setShowContextMenu(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit record
              </span>
            </div>
          )}
          <div
            className="context-menu-item"
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onClick={() => { handleMenuCopyLink(); setShowContextMenu(false); }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Copy link
            </span>
          </div>
          {auth.id === input.user && (
            <div
              className="context-menu-item"
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onClick={() => { handleMenuTogglePin(); setShowContextMenu(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z" />
                  <line x1="12" y1="16" x2="12" y2="22" />
                </svg>
                {input.pinned ? 'Unpin' : 'Pin to top'}
              </span>
            </div>
          )}

          {/* Divider + Danger Zone */}
          {auth.id === input.user && (
            <>
              <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '4px 0' }} />
              <div
                className="context-menu-item-delete"
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#A32D2D'
                }}
                onClick={() => { handleMenuDelete(); setShowContextMenu(false); }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </span>
                <span style={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(163, 45, 45, 0.12)',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  color: '#A32D2D'
                }}>D</span>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        size='lg'
        style={{ maxWidth: '1000px', width: '100%' }}
        className='my-modal'
      >
        <ModalHeader
          style={{
            borderBottom: '1px solid #c5d8ec',
            padding: '12px 16px'
          }}
          close={
            <button
              type="button"
              onClick={toggleModal}
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '6px',
                background: 'rgba(255,255,255,0.6)',
                border: 'none',
                color: '#4a6a8a',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          }
        >
          <span style={{ fontWeight: 500, fontSize: '15px' }}>Update Input</span>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-date-${input._id}`}
                  name='edit-date'
                  value={formData.date}
                  onChange={handleInputChange('date')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.date ? '20px' : '12px',
                    paddingBottom: formData.date ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-date-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.date ? '6px' : '50%',
                    transform: formData.date ? 'none' : 'translateY(-50%)',
                    fontSize: formData.date ? '10px' : '14px',
                    fontWeight: formData.date ? '500' : '400',
                    textTransform: formData.date ? 'uppercase' : 'none',
                    color: formData.date ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Date
                </label>
                {formData.date && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, date: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-place-${input._id}`}
                  name='edit-place'
                  value={formData.place}
                  onChange={handleInputChange('place')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.place ? '20px' : '12px',
                    paddingBottom: formData.place ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-place-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.place ? '6px' : '50%',
                    transform: formData.place ? 'none' : 'translateY(-50%)',
                    fontSize: formData.place ? '10px' : '14px',
                    fontWeight: formData.place ? '500' : '400',
                    textTransform: formData.place ? 'uppercase' : 'none',
                    color: formData.place ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Place
                </label>
                {formData.place && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, place: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-person-${input._id}`}
                  name='edit-person'
                  value={formData.person}
                  onChange={handleInputChange('person')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.person ? '20px' : '12px',
                    paddingBottom: formData.person ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-person-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.person ? '6px' : '50%',
                    transform: formData.person ? 'none' : 'translateY(-50%)',
                    fontSize: formData.person ? '10px' : '14px',
                    fontWeight: formData.person ? '500' : '400',
                    textTransform: formData.person ? 'uppercase' : 'none',
                    color: formData.person ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Person
                </label>
                {formData.person && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, person: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-subject-${input._id}`}
                  name='edit-subject'
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.subject ? '20px' : '12px',
                    paddingBottom: formData.subject ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-subject-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.subject ? '6px' : '50%',
                    transform: formData.subject ? 'none' : 'translateY(-50%)',
                    fontSize: formData.subject ? '10px' : '14px',
                    fontWeight: formData.subject ? '500' : '400',
                    textTransform: formData.subject ? 'uppercase' : 'none',
                    color: formData.subject ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Subject
                </label>
                {formData.subject && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, subject: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                  id={`edit-reason-${input._id}`}
                  name='edit-reason'
                  value={formData.reason}
                  onChange={handleInputChange('reason')}
                  className='form-control'
                  rows={3}
                  style={{
                    paddingTop: formData.reason ? '24px' : '12px',
                    paddingBottom: '8px',
                    paddingLeft: '12px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    resize: 'vertical'
                  }}
                />
                <label
                  htmlFor={`edit-reason-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.reason ? '6px' : '12px',
                    fontSize: formData.reason ? '10px' : '14px',
                    fontWeight: formData.reason ? '500' : '400',
                    textTransform: formData.reason ? 'uppercase' : 'none',
                    color: formData.reason ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Reason
                </label>
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                  id={`edit-condition-${input._id}`}
                  name='edit-condition'
                  value={formData.condition}
                  onChange={handleInputChange('condition')}
                  className='form-control'
                  rows={2}
                  style={{
                    paddingTop: formData.condition ? '24px' : '12px',
                    paddingBottom: '8px',
                    paddingLeft: '12px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    resize: 'vertical'
                  }}
                />
                <label
                  htmlFor={`edit-condition-${input._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.condition ? '6px' : '12px',
                    fontSize: formData.condition ? '10px' : '14px',
                    fontWeight: formData.condition ? '500' : '400',
                    textTransform: formData.condition ? 'uppercase' : 'none',
                    color: formData.condition ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Condition
                </label>
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id={`edit-data-${input._id}`}
                    name='edit-data'
                    value={formData.data}
                    onChange={handleInputChange('data')}
                    className='form-control'
                    style={{
                      paddingRight: '30px',
                      paddingTop: formData.data ? '20px' : '12px',
                      paddingBottom: formData.data ? '6px' : '12px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <label
                    htmlFor={`edit-data-${input._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: formData.data ? '6px' : '50%',
                      transform: formData.data ? 'none' : 'translateY(-50%)',
                      fontSize: formData.data ? '10px' : '14px',
                      fontWeight: formData.data ? '500' : '400',
                      textTransform: formData.data ? 'uppercase' : 'none',
                      color: formData.data ? '#185FA5' : '#999',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    Data
                  </label>
                  {formData.data && (
                    <button
                      type='button'
                      onClick={() => setFormData(prev => ({ ...prev, data: '' }))}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#8aabcc',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id={`edit-unit-${input._id}`}
                    name='edit-unit'
                    value={formData.unit}
                    onChange={handleInputChange('unit')}
                    className='form-control'
                    style={{
                      paddingRight: '30px',
                      paddingTop: formData.unit ? '20px' : '12px',
                      paddingBottom: formData.unit ? '6px' : '12px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <label
                    htmlFor={`edit-unit-${input._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: formData.unit ? '6px' : '50%',
                      transform: formData.unit ? 'none' : 'translateY(-50%)',
                      fontSize: formData.unit ? '10px' : '14px',
                      fontWeight: formData.unit ? '500' : '400',
                      textTransform: formData.unit ? 'uppercase' : 'none',
                      color: formData.unit ? '#185FA5' : '#999',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    Unit
                  </label>
                  {formData.unit && (
                    <button
                      type='button'
                      onClick={() => setFormData(prev => ({ ...prev, unit: '' }))}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#8aabcc',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </Row>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button
                type='button'
                onClick={toggleModal}
                style={{
                  background: 'rgba(255,255,255,0.6)',
                  border: '0.5px solid #c5d8ec',
                  color: '#4a6a8a',
                  fontWeight: 500,
                  padding: '7px 22px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type='submit'
                style={{
                  background: '#185FA5',
                  border: 'none',
                  color: '#fff',
                  fontWeight: 500,
                  padding: '7px 22px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Update
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <td
        className={'sticky-td'}
        onClick={event => handleClick(event, input._id)}
        style={{ textAlign: 'center', verticalAlign: 'top' }}
      >
        <button
          onClick={handleMenuButtonClick}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '16px',
            color: '#666',
            lineHeight: 1
          }}
          title="More actions"
        >
          ☰
        </button>
      </td>
      <td
        name='date'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isDateEditing ? (
          <Input
            name='date'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(input.date, searchKeywords)
        )}
      </td>
      <td
        name='place'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isPlaceEditing ? (
          <Input
            name='place'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : input.place.indexOf('http') === 0 ||
          input.place.indexOf('/') === 0 ? (
          input.place ? (
            input.place.indexOf('http') === 0 ? (
              <a
                href={input.place}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : input.place.indexOf('www') !== -1 ? (
                  <span>
                    {input.place.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : input.place.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  <span>
                    {input.place.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                ) : input.place.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  input.place.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {input.place.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : (
                  <span>
                    {input.place.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${input.place}`}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <code>{highlightText(input.place, searchKeywords)}</code>
        )}
      </td>
      <td
        name='person'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isPersonEditing ? (
          <Input
            name='person'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(input.person, searchKeywords)
        )}
      </td>
      <td
        name='subject'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isSubjectEditing ? (
          <Input
            name='subject'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          <b>{highlightText(input.subject, searchKeywords)}</b>
        )}
      </td>
      <td
        name='reason'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}        data-cellid={input._id + '|reason'}
        style={{ position: 'relative', maxWidth: getColumnWidth('reason') }}
      >
        <div style={{ width: getColumnWidth('reason') }}>
          {auth.id === input.user && isReasonEditing ? (
            <textarea
              name='reason'
              value={tempValue}
              onChange={handleChange}
              onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
                setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
              })}
              onDoubleClick={selectImageTagOnDoubleClick}
              onFocus={e => {
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              autoFocus
              style={{ width: '100%', overflow: 'hidden', resize: 'vertical' }}
              rows={Math.max(5, tempValue.split('\n').length)}
            />
          ) : (
            <div
              key={`reason-${input._id}-${contentVersion}-${htmlResumeRevision}`}
              name='reason'
              onDoubleClick={handleDoubleClick}
              onClick={handleImageClick}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingReason !== null ? pendingReason : input.reason), searchKeywords))
              }}
            />
          )}
        </div>
        <ColumnResizeHandle onMouseDown={startResize('reason')} />
      </td>
      <td
        name='condition'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}        data-cellid={input._id + '|condition'}
        style={{ position: 'relative', maxWidth: getColumnWidth('condition') }}
      >
        <div style={{ width: getColumnWidth('condition') }}>
          {auth.id === input.user && isConditionEditing ? (
            <textarea
              name='condition'
              type='text'
              value={tempValue}
              onChange={handleChange}
              onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
                setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
              })}
              onDoubleClick={selectImageTagOnDoubleClick}
              onFocus={e => {
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              autoFocus
              style={{ width: '100%', overflow: 'hidden', resize: 'vertical' }}
              rows={Math.max(5, tempValue.split('\n').length)}
            />
          ) : (
            <div
              key={`condition-${input._id}-${contentVersion}-${htmlResumeRevision}`}
              name='condition'
              onDoubleClick={handleDoubleClick}
              onClick={handleImageClick}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingCondition !== null ? pendingCondition : input.condition), searchKeywords))
              }}
            />
          )}
        </div>
        <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />
        <ColumnResizeHandle onMouseDown={startResize('condition')} />
      </td>
      <td
        name='data'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isDataEditing ? (
          <Input
            name='data'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : input.data.indexOf('http') === 0 ||
          input.data.indexOf('/') === 0 ? (
          input.data ? (
            input.data.indexOf('http') === 0 ? (
              <a
                href={input.data}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : input.data.indexOf('www') !== -1 ? (
                  <span>
                    {input.data.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : input.data.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  <span>
                    {input.data.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                ) : input.data.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  input.data.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {input.data.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : (
                  <span>
                    {input.data.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${input.data}`}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <code>{highlightText(input.data, searchKeywords)}</code>
        )}
      </td>
      <td
        name='unit'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, input._id)}      >
        {auth.id === input.user && isUnitEditing ? (
          <Input
            name='unit'
            type='text'
            value={tempValue}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(input.unit, searchKeywords)
        )}
      </td>
    </tr>
  );
}

export let input_num;

export default function ListData (props) {
  const tableRef = useRef(null);
  const containerRef = useRef(null);
  const [pinEnabled, setPinEnabled] = usePinEnabled();
  const [manualResizeEnabled, setManualResizeEnabled] = useManualResizeEnabled();
  const { getColumnWidth, startResize } = useColumnWidths('columnWidths.data', { enabled: manualResizeEnabled });
  const [searchParams] = useSearchParams();
  const dispatchForDeepLink = useDispatch();
  const deepLinkHandledIdRef = useRef(null);
  // Computed during render (see pageById below); applied in an effect rather
  // than during render itself, so it isn't affected by StrictMode
  // double-invoking the render body.
  const pendingDeepLinkPageRef = useRef(null);

  useEffect(() => {
    const pending = pendingDeepLinkPageRef.current;
    if (pending && deepLinkHandledIdRef.current !== pending.id) {
      deepLinkHandledIdRef.current = pending.id;
      setCurrentPage(pending.page);
    }
  });

  // Check if redux-persist has rehydrated
  const _persist = useSelector(state => state._persist);
  const isRehydrated = _persist?.rehydrated;

  // Deep linking: scroll to and highlight the item when URL has ?id=
  // parameter. Which page to jump to is figured out live during render (see
  // pageById below) rather than from a stored page number, since a fixed
  // page number would go stale as new records shift everything's position.
  useEffect(() => {
    const itemId = searchParams.get('id');
    if (itemId && !props.isLoading) {
      // Poll for the row instead of a fixed delay - re-sorting/paginating a
      // large list can take longer than any one fixed timeout to reach the DOM.
      let attempts = 0;
      const findAndScrollToRow = () => {
        const row = document.querySelector(`tr[data-row-id="${itemId}"]`);
        if (row) {
          // Scroll to the row
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the row temporarily
          dispatchForDeepLink({
            type: 'SET_ITEM_COLOR',
            payload: { id: itemId, color: '#fffacd' } // Light yellow highlight
          });
          // Flash effect
          setTimeout(() => {
            row.style.transition = 'background-color 0.5s';
            dispatchForDeepLink({
              type: 'SET_ITEM_COLOR',
              payload: { id: itemId, color: '#d6eaf8' } // Light blue selection
            });
          }, 500);
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(findAndScrollToRow, 100);
        }
      };
      setTimeout(findAndScrollToRow, 100);
    }
  }, [searchParams, props.isLoading, dispatchForDeepLink]);

  // Reset to page 1 whenever the search keyword actually changes, so a
  // search that only matches items on an earlier page doesn't leave the view
  // stuck on a now-empty page. Compares against the previous value (rather
  // than a "have I run once" flag) so it stays a no-op on mount even under
  // StrictMode's double-invoked effects, and doesn't clobber a deep link's
  // page jump.
  const previousKeywordRef = useRef(props.keyword);
  useEffect(() => {
    if (previousKeywordRef.current !== props.keyword) {
      setCurrentPage(1);
    }
    previousKeywordRef.current = props.keyword;
  }, [props.keyword]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Tab') {
        event.preventDefault();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  // A previous "fix" for iOS Safari resetting input values after long tab
  // suspension used to live here: it saved every embedded input/textarea's
  // value to localStorage keyed only by its DOM position (row id + cell name
  // + index within the cell), then blindly wrote those saved values back in
  // on visibilitychange/pageshow (i.e. whenever the phone wakes up or the
  // tab regains focus) - with no check on how old the saved value was or
  // whether it still matched what actually belonged in that position. Once
  // an entry for a given position existed, it would keep getting restored
  // over whatever was actually, correctly rendered there, including a stale
  // copy from days/weeks earlier - which is exactly the "old content comes
  // back after waking the phone" bug. Removed rather than patched: the
  // official edit flow already avoids this entirely (it's a React-controlled
  // textarea, unaffected by iOS resetting the raw DOM value), and this
  // one-time cleanup just clears out any stale snapshot it left behind.
  useEffect(() => {
    localStorage.removeItem('kirkInputValues');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        // console.log('tableRef.current.scrollLeft: ', tableRef.current.scrollLeft);
      }
    };

    if (tableRef.current) {
      const tableElement = tableRef.current;
      tableElement.addEventListener('scroll', handleScroll);

      return () => {
        tableElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const isFullContentShown = useSelector(
    state => state.fieldButtons.isFullContentShown
  );

  const toggledInputFieldButton = useSelector(
    state => state.fieldButtons.toggledInputFieldButton
  );

  const isInputIdAscending = useSelector(
    state => state.fieldButtons.isInputIdAscending
  );
  const isInputDateAscending = useSelector(
    state => state.fieldButtons.isInputDateAscending
  );
  const isInputPlaceAscending = useSelector(
    state => state.fieldButtons.isInputPlaceAscending
  );
  const isInputPersonAscending = useSelector(
    state => state.fieldButtons.isInputPersonAscending
  );
  const isInputSubjectAscending = useSelector(
    state => state.fieldButtons.isInputSubjectAscending
  );
  const isInputReasonAscending = useSelector(
    state => state.fieldButtons.isInputReasonAscending
  );
  const isInputConditionAscending = useSelector(
    state => state.fieldButtons.isInputConditionAscending
  );
  const isInputDataAscending = useSelector(
    state => state.fieldButtons.isInputDataAscending
  );
  const isInputUnitAscending = useSelector(
    state => state.fieldButtons.isInputUnitAscending
  );

  const dispatch = useDispatch();

  const toggleIsInputIdAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_ID_ASCENDING' });
  };
  const toggleIsInputDateAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_DATE_ASCENDING' });
  };
  const toggleIsInputPlaceAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_PLACE_ASCENDING' });
  };
  const toggleIsInputPersonAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_PERSON_ASCENDING' });
  };
  const toggleIsInputSubjectAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_SUBJECT_ASCENDING' });
  };
  const toggleIsInputReasonAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_REASON_ASCENDING' });
  };
  const toggleIsInputConditionAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_CONDITION_ASCENDING' });
  };
  const toggleIsInputDataAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_DATA_ASCENDING' });
  };
  const toggleIsInputUnitAscending = () => {
    dispatch({ type: 'TOGGLE_IS_INPUT_UNIT_ASCENDING' });
  };
  const toggleIsFullContentShown = () => {
    dispatch({ type: 'TOGGLE_IS_FULL_CONTENT_SHOWN' });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [inputListPerPage] = useState(100);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(null);
  const [savedPage, setSavedPage] = useState(null);
  const [isAtTop, setIsAtTop] = useState(true);

  const paginate = pageNumber => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show/hide scroll-to-top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down more than 1000px
      setShowScrollButton(window.scrollY > 1000);
      // Consider "at top" when under 1000px threshold
      setIsAtTop(window.scrollY < 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle between top and saved position
  const toggleScrollPosition = () => {
    if (isAtTop && savedScrollPosition !== null) {
      // DOWN arrow clicked - go back to the saved page and scroll position
      if (savedPage !== null && savedPage !== currentPage) {
        setCurrentPage(savedPage);
        // Wait for the restored page's content to render before scrolling
        // to a non-zero offset, or the browser clamps to the old content height
        setTimeout(() => {
          window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
        }, 100);
      } else {
        window.scrollTo({
          top: savedScrollPosition,
          behavior: 'smooth'
        });
      }
      setSavedScrollPosition(null);
      setSavedPage(null);
    } else {
      // UP arrow clicked - go to the very top of the list (page 1), not just
      // the top of whichever page happens to be showing
      if (savedScrollPosition === null) {
        // Only save position if none exists (keep previous position)
        setSavedScrollPosition(window.scrollY);
        setSavedPage(currentPage);
      }
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Floating button styles - simple arrow, no circle
  // Show button when scrolled down OR when at top with a saved position to go back to
  const shouldShowButton = showScrollButton || (isAtTop && savedScrollPosition !== null);
  const scrollButtonStyle = {
    position: 'fixed',
    top: '70px',
    right: '20px',
    zIndex: 1000,
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    cursor: 'pointer',
    display: shouldShowButton ? 'block' : 'none',
    fontSize: '28px',
    padding: '10px',
    transition: 'opacity 0.3s'
  };

  if (props.isLoading) {
    return (
      <div className='container'>
        <div className='row'>
          <Loading version={props.version} />
        </div>
      </div>
    );
  } else if (props.errMess) {
    return (
      <div className='container'>
        <div className='row'>
          <h4>{props.errMess}</h4>
        </div>
      </div>
    );
  } else if (props.keyword !== '') {
    const keywordAndArray = props.keyword.split('|');
    var keywords = [];
    var exwords = [];
    for (let k = 0; k < keywordAndArray.length; k++) {
      let keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
      var keywords_temp = [];
      var exwords_temp = [];
      let j = 0;
      let m = 0;
      for (let i = 0; i < keywordArray.length; i++) {
        if ((keywordArray[i] !== ' ') & (keywordArray[i] !== '')) {
          if (keywordArray[i].split('')[0] === '!') {
            if (keywordArray[i].length === 2) {
              exwords_temp[m] = keywordArray[i].split('').splice(1).join('');
            } else {
              exwords_temp[m] = keywordArray[i]
                .toLowerCase()
                .split('')
                .splice(1)
                .join('');
            }
            m = m + 1;
          } else {
            if (keywordArray[i].length === 1) {
              keywords_temp[j] = keywordArray[i];
            } else {
              keywords_temp[j] = keywordArray[i].toLowerCase();
            }
            j = j + 1;
          }
        }
      }
      keywords.push(keywords_temp);
      exwords.push(exwords_temp);
    }
    var foundInputs = [];
    for (let i = 0; i < keywords.length; i++) {
      const foundInputs_temp = props.inputs.filter(curr => {
        const test = keywords[i].map(keyword => {
          if (keyword.length === 1) {
            return (
              curr.date.includes(keyword) ||
              curr.place.includes(keyword) ||
              curr.person.includes(keyword) ||
              curr.subject.includes(keyword) ||
              curr.reason.includes(keyword) ||
              curr.condition.includes(keyword) ||
              curr.data.includes(keyword) ||
              curr.unit.includes(keyword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(keyword) ||
              curr.place.toLowerCase().includes(keyword) ||
              curr.person.toLowerCase().includes(keyword) ||
              curr.subject.toLowerCase().includes(keyword) ||
              curr.reason.toLowerCase().includes(keyword) ||
              curr.condition.toLowerCase().includes(keyword) ||
              curr.data.toLowerCase().includes(keyword) ||
              curr.unit.toLowerCase().includes(keyword)
            );
          }
        });
        return test.every(logic => logic === true);
      });
      foundInputs = foundInputs.concat(foundInputs_temp);
    }
    var foundInputs2 = [];
    for (let i = 0; i < exwords.length; i++) {
      const foundInputs2_temp = foundInputs.filter(curr => {
        const test = exwords[i].map(exword => {
          if (exword.length === 1) {
            return (
              curr.date.includes(exword) ||
              curr.place.includes(exword) ||
              curr.person.includes(exword) ||
              curr.subject.includes(exword) ||
              curr.reason.includes(exword) ||
              curr.condition.includes(exword) ||
              curr.data.includes(exword) ||
              curr.unit.includes(exword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(exword) ||
              curr.place.toLowerCase().includes(exword) ||
              curr.person.toLowerCase().includes(exword) ||
              curr.subject.toLowerCase().includes(exword) ||
              curr.reason.toLowerCase().includes(exword) ||
              curr.condition.toLowerCase().includes(exword) ||
              curr.data.toLowerCase().includes(exword) ||
              curr.unit.toLowerCase().includes(exword)
            );
          }
        });
        return test.every(logic => logic === false);
      });
      foundInputs2 = foundInputs2.concat(foundInputs2_temp);
      foundInputs2.sort((a, b) => {
        return a._id.localeCompare(b._id);
      });
    }

    const conv = inputDate => {
      let day = inputDate.split(' ').find(element => element.includes('-'));
      if (day) {
        const len = day.split('-')[0].length;

        if (!day.split('-')[0]) {
          return '';
        }

        if (!day.split('-')[1]) {
          return '';
        }

        if (!day.split('-')[2]) {
          return '';
        }

        const firstnumber = day.split('-')[0].padStart(2, '0');
        const secondnumber = day.split('-')[1].padStart(2, '0');
        const thirdnumber = day.split('-')[2].padStart(2, '0');

        let time = inputDate.split(' ').find(element => element.includes(':'));
        if (!time) {
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} 00:00:00`;
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} 00:00:00`;
          }
        } else {
          const [hour, minute] = time
            .split(':')
            .map(num => num.padStart(2, '0'));
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} ${hour}:${minute}:00`;
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} ${hour}:${minute}:00`;
          }
        }
      }

      if (!day) {
        return '';
      }
      return day;
    };

    switch (toggledInputFieldButton) {
      case 'ID':
        if (isInputIdAscending === false) {
          foundInputs2.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
        break;
      case 'DATE':
        if (isInputDateAscending === false) {
          foundInputs2.sort((a, b) => {
            return conv(a.date).localeCompare(conv(b.date));
          });
        } else {
          foundInputs2.sort((a, b) => {
            return conv(b.date).localeCompare(conv(a.date));
          });
        }
        break;
      case 'PLACE':
        if (isInputPlaceAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.place.localeCompare(b.place);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.place.localeCompare(a.place);
          });
        }
        break;
      case 'PERSON':
        if (isInputPersonAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.person.localeCompare(b.person);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.person.localeCompare(a.person);
          });
        }
        break;
      case 'SUBJECT':
        if (isInputSubjectAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.subject.localeCompare(b.subject);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.subject.localeCompare(a.subject);
          });
        }
        break;
      case 'REASON':
        if (isInputReasonAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.reason.localeCompare(b.reason);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.reason.localeCompare(a.reason);
          });
        }
        break;
      case 'CONDITION':
        if (isInputConditionAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.condition.localeCompare(b.condition);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.condition.localeCompare(a.condition);
          });
        }
        break;
      case 'DATA':
        if (isInputDataAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.data
              .replaceAll(',', '')
              .localeCompare(b.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.data
              .replaceAll(',', '')
              .localeCompare(a.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'UNIT':
        if (isInputUnitAscending === false) {
          foundInputs2.sort((a, b) => {
            return a.unit.localeCompare(b.unit);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b.unit.localeCompare(a.unit);
          });
        }
        break;
      default:
        if (isInputIdAscending === false) {
          foundInputs2.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          foundInputs2.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
    }

    function onlyUnique (value, index, array) {
      return array.indexOf(value) === index;
    }
    foundInputs2 = foundInputs2.filter(onlyUnique);

    // Show newest-first, then float pinned items to the very top regardless
    // of whatever column/direction is currently sorted (unless pinning is
    // switched off, in which case just show the plain sort order)
    foundInputs2 = pinEnabled
      ? orderWithPinnedFirst(foundInputs2.reverse())
      : foundInputs2.reverse();

    const csvData = foundInputs2;

    // Extract keywords for highlighting
    const searchKeywords = extractSearchKeywords(props.keyword);

    // Page number each item currently lands on (list is already in final display order)
    const pageById = {};
    foundInputs2.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / inputListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const inputList = foundInputs2.map(input => {
      return (
        <Fragment key={input._id}>
          <RenderInputTable
            input={input}
            deleteInput={props.deleteInput}
            updateInput={props.updateInput}
            resetInputForm={props.resetInputForm}
            auth={props.auth}
            updatingId={props.updatingId}
            isFullContentShown={isFullContentShown}
            toggleIsFullContentShown={toggleIsFullContentShown}
            saveScrollPosition={props.saveScrollPosition}
            restoreScrollPosition={props.restoreScrollPosition}
            searchKeywords={searchKeywords}
            keyword={props.keyword}
            view={props.view}
            getColumnWidth={getColumnWidth}
            startResize={startResize}
            htmlResumeRevision={props.htmlResumeRevision}
          />
        </Fragment>
      );
    });

    input_num = inputList.length;

    const indexOfLastInputList = currentPage * inputListPerPage;
    const indexOfFirstInputList = indexOfLastInputList - inputListPerPage;
    const currentInputList = inputList
      .slice(indexOfFirstInputList, indexOfLastInputList);

    return (
      <div ref={containerRef}>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    <Button
                      outline
                      color={
                        isInputIdAscending === false ? 'primary' : 'secondary'
                      }
                      onClick={toggleIsInputIdAscending}
                      id='TooltipExample'
                      data-tip='Your tooltip text'
                    >
                      {input_num} items
                    </Button>
                    <Tooltip id='TooltipExample' />
                    <Button
                      outline
                      onClick={toggleIsFullContentShown}
                      style={{ marginLeft: '10px' }}
                    >
                      {isFullContentShown ? '...' : 'Show all'}
                    </Button>
                    <Button
                      outline
                      size='sm'
                      color={manualResizeEnabled ? 'primary' : 'secondary'}
                      onClick={() => setManualResizeEnabled(!manualResizeEnabled)}
                      style={{ marginLeft: '10px' }}
                      title={manualResizeEnabled ? 'Manual column widths are on: drag a column border to resize it. Click to turn off and auto-size again.' : 'Manual column widths are off: columns auto-size to their content. Click to turn on and drag column borders to resize.'}
                    >
                      <span className='fa fa-arrows-h'></span>
                    </Button>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                <div
                  className='table-responsive'
                  ref={props.scrollContainerRef}
                  onScroll={props.saveScrollPosition}
                  style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                >
                  <Table
                    bordered
                    hover
                    striped
                    style={{
                      wordWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    <thead>
                      <tr>
                        <th></th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputDateAscending}
                          >
                            date
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputPlaceAscending}
                          >
                            place
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputPersonAscending}
                          >
                            person
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputSubjectAscending}
                          >
                            subject
                          </Button>
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('reason') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsInputReasonAscending}
                            >
                              reason
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsInputConditionAscending}
                            >
                              condition
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />
                          <ColumnResizeHandle onMouseDown={startResize('condition')} />
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputDataAscending}
                          >
                            data
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputUnitAscending}
                          >
                            unit
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{currentInputList}</tbody>
                  </Table>
                  <div className='text-center text-muted py-2'>
                    <small>Ver {props.version} © 2022-2026 Cage H.</small>
                  </div>
                </div>
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={inputListPerPage}
          totalInputList={inputList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else if (props.inputs !== null) {
    let sortedInputs = [...props.inputs];

    const conv = inputDate => {
      let day = inputDate.split(' ').find(element => element.includes('-'));
      if (day) {
        const len = day.split('-')[0].length;

        if (!day.split('-')[0]) {
          return '';
        }

        if (!day.split('-')[1]) {
          return '';
        }

        if (!day.split('-')[2]) {
          return '';
        }

        const firstnumber = day.split('-')[0].padStart(2, '0');
        const secondnumber = day.split('-')[1].padStart(2, '0');
        const thirdnumber = day.split('-')[2].padStart(2, '0');

        let time = inputDate.split(' ').find(element => element.includes(':'));
        if (!time) {
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} 00:00:00`;
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} 00:00:00`;
          }
        } else {
          const [hour, minute] = time
            .split(':')
            .map(num => num.padStart(2, '0'));
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} ${hour}:${minute}:00`;
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} ${hour}:${minute}:00`;
          }
        }
      }

      if (!day) {
        return '';
      }

      return day;
    };

    switch (toggledInputFieldButton) {
      case 'ID':
        if (isInputIdAscending === false) {
          sortedInputs.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
        break;
      case 'DATE':
        console.log(
          '📅 DATE case - isInputDateAscending:',
          isInputDateAscending
        );
        if (isInputDateAscending === false) {
          sortedInputs.sort((a, b) => {
            return conv(a.date).localeCompare(conv(b.date));
          });
        } else {
          sortedInputs.sort((a, b) => {
            return conv(b.date).localeCompare(conv(a.date));
          });
        }
        break;
      case 'PLACE':
        if (isInputPlaceAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.place.localeCompare(b.place);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.place.localeCompare(a.place);
          });
        }
        break;
      case 'PERSON':
        if (isInputPersonAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.person.localeCompare(b.person);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.person.localeCompare(a.person);
          });
        }
        break;
      case 'SUBJECT':
        if (isInputSubjectAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.subject.localeCompare(b.subject);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.subject.localeCompare(a.subject);
          });
        }
        break;
      case 'REASON':
        if (isInputReasonAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.reason.localeCompare(b.reason);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.reason.localeCompare(a.reason);
          });
        }
        break;
      case 'CONDITION':
        if (isInputConditionAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.condition.localeCompare(b.condition);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.condition.localeCompare(a.condition);
          });
        }
        break;
      case 'DATA':
        if (isInputDataAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.data
              .replaceAll(',', '')
              .localeCompare(b.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.data
              .replaceAll(',', '')
              .localeCompare(a.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'UNIT':
        if (isInputUnitAscending === false) {
          sortedInputs.sort((a, b) => {
            return a.unit.localeCompare(b.unit);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b.unit.localeCompare(a.unit);
          });
        }
        break;
      default:
        if (isInputIdAscending === false) {
          sortedInputs.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          sortedInputs.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
    }

    // Show newest-first, then float pinned items to the very top regardless
    // of whatever column/direction is currently sorted (unless pinning is
    // switched off, in which case just show the plain sort order)
    sortedInputs = pinEnabled
      ? orderWithPinnedFirst(sortedInputs.reverse())
      : sortedInputs.reverse();

    const pageById = {};
    sortedInputs.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / inputListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const inputList = sortedInputs.map(input => {
      return (
        <Fragment key={input._id}>
          <RenderInputTable
            input={input}
            deleteInput={props.deleteInput}
            updateInput={props.updateInput}
            resetInputForm={props.resetInputForm}
            auth={props.auth}
            updatingId={props.updatingId}
            isFullContentShown={isFullContentShown}
            toggleIsFullContentShown={toggleIsFullContentShown}
            saveScrollPosition={props.saveScrollPosition}
            restoreScrollPosition={props.restoreScrollPosition}
            searchKeywords={[]}
            keyword={props.keyword}
            view={props.view}
            getColumnWidth={getColumnWidth}
            startResize={startResize}
            htmlResumeRevision={props.htmlResumeRevision}
          />
        </Fragment>
      );
    });

    input_num = inputList.length;
    const csvData = sortedInputs;

    const indexOfLastInputList = currentPage * inputListPerPage;
    const indexOfFirstInputList = indexOfLastInputList - inputListPerPage;
    const currentInputList = inputList
      .slice(indexOfFirstInputList, indexOfLastInputList);

    return (
      <div ref={containerRef}>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    <Button
                      outline
                      color={
                        isInputIdAscending === false ? 'primary' : 'secondary'
                      }
                      onClick={toggleIsInputIdAscending}
                    >
                      {input_num} items
                    </Button>
                    <Button
                      outline
                      onClick={toggleIsFullContentShown}
                      style={{ marginLeft: '10px' }}
                    >
                      {isFullContentShown ? '...' : 'Show all'}
                    </Button>
                    <Button
                      outline
                      size='sm'
                      color={manualResizeEnabled ? 'primary' : 'secondary'}
                      onClick={() => setManualResizeEnabled(!manualResizeEnabled)}
                      style={{ marginLeft: '10px' }}
                      title={manualResizeEnabled ? 'Manual column widths are on: drag a column border to resize it. Click to turn off and auto-size again.' : 'Manual column widths are off: columns auto-size to their content. Click to turn on and drag column borders to resize.'}
                    >
                      <span className='fa fa-arrows-h'></span>
                    </Button>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                <div
                  className='table-responsive'
                  ref={props.scrollContainerRef}
                  onScroll={props.saveScrollPosition}
                  style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                >
                  <Table
                    bordered
                    hover
                    striped
                    className='table'
                    style={{
                      wordWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    <thead>
                      <tr>
                        <th></th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputDateAscending}
                          >
                            date
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputPlaceAscending}
                          >
                            place
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputPersonAscending}
                          >
                            person
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputSubjectAscending}
                          >
                            subject
                          </Button>
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('reason') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsInputReasonAscending}
                            >
                              reason
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsInputConditionAscending}
                            >
                              condition
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />
                          <ColumnResizeHandle onMouseDown={startResize('condition')} />
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputDataAscending}
                          >
                            data
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsInputUnitAscending}
                          >
                            unit
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{currentInputList}</tbody>
                  </Table>
                  <div className='text-center text-muted py-2'>
                    <small>Ver {props.version} © 2022-2026 Cage H.</small>
                  </div>
                </div>
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={inputListPerPage}
          totalInputList={inputList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else {
    return <div></div>;
  }
}
